<template>
  <div class="vcv-tiptap" :class="{ error: isEmpty }">
    <menu-bar :editor="editor" :variables="variables" :toolbar="toolbar">
      <template slot="toolbar"><slot name="toolbar"></slot></template>
    </menu-bar>

    <editor-content :editor="editor" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import MenuBar from './VcvTiptapMenu';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';

export default {
  components: {
    EditorContent,
    MenuBar
  },

  props: {
    value: {
      type: String,
      default: ''
    },

    variables: {
      type: [Array],
      default: () => []
    },

    isEmpty: {
      type: Boolean,
      default: false
    },

    toolbar: {
      type: [Array, String],
      default: () => []
    },

    imgPasteRestricted: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      editor: {}
    };
  },

  watch: {
    value(value) {
      // HTML
      const isSame = this.editor.getHTML() === value;

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      if (isSame) {
        return;
      }

      // this.editor.commands.setContent(value, false);
      this.editor.commands.clearContent();
      this.editor.commands.insertContent(value);
    }
  },

  created() {
    this.editor = new Editor({
      content: this.value,
      editable: true,
      extensions: [
        StarterKit.configure({
          paragraph: {
            HTMLAttributes: {
              style: 'margin-bottom: 16px; padding-top: 0; padding-bottom: 0;'
            }
          }
        }),
        Underline,
        Link,
        Image.configure({ inline: true }),
        Table.configure({
          resizable: true,
          HTMLAttributes: {
            class: 'vcv-tiptap-table'
          }
        }),
        TableCell,
        TableHeader,
        TableRow
      ],

      onUpdate: () => {
        // HTML
        this.$emit('input', this.editor.getHTML());
        // JSON
        // this.$emit('input', this.editor.getJSON())
        this.$emit('update');
      },

      onSelectionUpdate: ({ editor }) => {
        this.$emit('selection-update');
      },

      onCreate: ({ editor }) => {
        if (this.imgPasteRestricted) {
          editor.setOptions({
            editorProps: {
              transformPastedHTML(html) {
                return html.replace(/<img.*?>/g, '');
              }
            }
          });
        }
      }
    });
  },

  beforeDestroy() {
    this.editor.destroy();
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.vcv-tiptap {
  ::v-deep .ProseMirror {
    @include mixin-text-main;
    padding: 15px;
    color: $black;
    background: $white;
    outline: none;
    min-height: 200px;
    border: 1px solid $grey-light;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    margin-bottom: 20px;
    transition: border 0.3s;

    &-focused {
      border: 1px solid $green-hover;
      transition: border 0.3s;
    }

    a {
      text-decoration: underline;
    }

    img {
      max-width: 100%;
      border: 1px solid transparent;
    }

    &-selectednode {
      border: 1px solid $blue !important;
      outline: none;
    }
  }

  &.error {
    ::v-deep .ProseMirror-focused {
      border: 1px solid $red;
      transition: border 0.3s;
    }
  }

  ::v-deep {
    .tableWrapper {
      overflow-x: auto;
    }

    .resize-cursor {
      cursor: ew-resize;
      cursor: col-resize;
    }

    ul,
    ul li {
      margin: 0;
      padding: 0;
      margin-left: 10px;
    }

    ol {
      counter-reset: item;
    }

    ol li {
      display: flex;
    }

    ol li:before {
      content: counter(item) '. ';
      counter-increment: item;
      width: 20px;
      display: inline-block;
    }

    table {
      border-collapse: collapse;
      table-layout: fixed;
      width: 100%;
      margin: 0;
      overflow: hidden;

      tr {
        height: auto !important;
      }

      td,
      th {
        min-width: 1em;
        border: 2px solid #ced4da;
        padding: 3px 5px;
        vertical-align: top;
        box-sizing: border-box;
        position: relative;
        @include mixin-text-main;

        > * {
          margin-bottom: 0;
        }
      }

      th {
        font-weight: bold;
        text-align: left;
        background-color: #f1f3f5;
      }

      .selectedCell:after {
        z-index: 2;
        position: absolute;
        content: '';
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background: rgba(200, 200, 255, 0.4);
        pointer-events: none;
      }

      .column-resize-handle {
        position: absolute;
        right: -2px;
        top: 0;
        bottom: -2px;
        width: 4px;
        background-color: #adf;
        pointer-events: none;
      }
    }
  }
}
</style>
